import {
  drugStatusInterface,
  portfolioDrugInterface,
} from 'Interfaces/FactsInterfaces/DrugPortfolioInterface';
import { makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import { drugPortfolioservices } from 'services/drugPortfolioServices';
import { deepCopy } from 'utils/deepCopy';

export class DrugPortfolioManagementStore {
  activeDrugs: drugStatusInterface[] = [];
  dormantDrugs: drugStatusInterface[] = [];
  inactiveDrugs: drugStatusInterface[] = [];
  isActiveDrugsLoading: boolean = false;
  isDormantDrugsLoading: boolean = false;
  isInactiveDrugsLoading: boolean = false;
  isDrugStatusUpdating: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  fetchportfolioDrugs = () => {
    try {
      this.getActiveDrugs();
      this.getDormantDrugs();
      this.getInactiveDrugs();
    } catch (error) {
      console.log(error);
    }
  };

  getActiveDrugs = async () => {
    this.isActiveDrugsLoading = true;
    try {
      const data = await drugPortfolioservices.getportfolioDrugs('Active');
      this.activeDrugs = this.convertDrugData(data);
    } catch (error) {
      console.log('DrugPortfolioManagementStore => getActiveDrugs => ', error);
    } finally {
      this.isActiveDrugsLoading = false;
    }
  };

  getDormantDrugs = async () => {
    this.isDormantDrugsLoading = true;
    try {
      const data = await drugPortfolioservices.getportfolioDrugs('Dormant');
      this.dormantDrugs = this.convertDrugData(data);
    } catch (error) {
      console.log('DrugPortfolioManagementStore => getDormantDrugs => ', error);
    } finally {
      this.isDormantDrugsLoading = false;
    }
  };

  getInactiveDrugs = async () => {
    this.isInactiveDrugsLoading = true;
    try {
      const data = await drugPortfolioservices.getportfolioDrugs('Inactive');
      this.inactiveDrugs = this.convertDrugData(data);
    } catch (error) {
      console.log('DrugPortfolioManagementStore => getInactiveDrugs => ', error);
    } finally {
      this.isInactiveDrugsLoading = false;
    }
  };

  convertDrugData = (data: portfolioDrugInterface[]): drugStatusInterface[] => {
    // Error Handling (remains the same)
    if (!Array.isArray(data) || data.length === 0) {
      console.error('Error: Invalid input data. Must be a non-empty array.');
      return [];
    }
    if (
      !data.every(
        item =>
          Object.prototype.hasOwnProperty.call(item, 'Component_ID') &&
          Object.prototype.hasOwnProperty.call(item, 'NDC11') &&
          Object.prototype.hasOwnProperty.call(item, 'loading') &&
          Object.prototype.hasOwnProperty.call(item, 'concept') &&
          Object.prototype.hasOwnProperty.call(item, 'gfc')
      )
    ) {
      console.error(
        'Error: Invalid data format. Each object must have Component_ID, NDC11, loading, concept, and gfc properties.'
      );
      return [];
    }

    const groupedData: { [key: string]: portfolioDrugInterface[] } = {};
    const nullConceptData: portfolioDrugInterface[] = [];

    data.forEach(item => {
      const { concept } = item;
      if (concept === null) {
        nullConceptData.push(item);
      } else {
        if (!groupedData[concept]) {
          groupedData[concept] = [];
        }
        groupedData[concept].push(item);
      }
    });

    const result: drugStatusInterface[] = Object.entries(groupedData).map(([concept, items]) => ({
      Concept: concept,
      GFC: items[0].gfc, // Assuming GFC is consistent within a concept
      NDC11_list: items.map(item => ({
        Component_ID: item.Component_ID,
        NDC11: item.NDC11,
        loading: item.loading,
      })),
    }));

    //Handle null concept data.  Note that GFC is undefined here, as it's not guaranteed to be consistent.
    if (nullConceptData.length > 0) {
      result.push({
        Concept: 'Null Concept',
        GFC: 0,
        NDC11_list: nullConceptData.map(item => ({
          Component_ID: item.Component_ID,
          NDC11: item.NDC11,
          loading: item.loading,
        })),
      });
    }

    return result;
  };

  saveGFCStatus = async (gfc: number, source: string, destination: string) => {
    const component_ids: number[] = [];
    const movedNdc11s: string[] = [];
    const originial_active_drugs = deepCopy(this.activeDrugs);
    const original_dormant_drugs = deepCopy(this.dormantDrugs);
    try {
      // moving the gfc from active state to dormant state
      if (source === 'Active' && destination === 'Dormant') {
        const active_gfc = this.activeDrugs.find(item => item.GFC === gfc);
        if (active_gfc) {
          active_gfc.NDC11_list.forEach(item => {
            // Use forEach for side effects
            item.loading = true;
            component_ids.push(item.Component_ID);
            movedNdc11s.push(item.NDC11);
          });
          if (this.dormantDrugs.some(item => item.GFC === active_gfc.GFC)) {
            this.dormantDrugs.map(item =>
              item.GFC === active_gfc.GFC
                ? (item.NDC11_list = [...item.NDC11_list, ...active_gfc.NDC11_list])
                : item
            );
          } else {
            this.dormantDrugs = [...this.dormantDrugs, active_gfc];
          }
          this.activeDrugs = this.activeDrugs.filter(item => item.GFC !== gfc);
        }
      }

      // moving the gfc from dormant to active
      if (source === 'Dormant' && destination === 'Active') {
        const dormant_gfc = this.dormantDrugs.find(item => item.GFC === gfc);
        if (dormant_gfc) {
          dormant_gfc.NDC11_list.forEach(item => {
            // Use forEach for side effects
            item.loading = true;
            component_ids.push(item.Component_ID);
            movedNdc11s.push(item.NDC11);
          });

          if (this.activeDrugs.filter(item => item.GFC === dormant_gfc.GFC).length !== 0) {
            this.activeDrugs.map(item =>
              item.GFC === dormant_gfc.GFC
                ? (item.NDC11_list = [...item.NDC11_list, ...dormant_gfc.NDC11_list])
                : item
            );
          } else {
            this.activeDrugs = [...this.activeDrugs, dormant_gfc];
          }

          this.dormantDrugs = this.dormantDrugs.filter(item => item.GFC !== gfc);
        }
      }

      //Added check to prevent empty payload from being sent to the server
      const payload = {
        component_ids: component_ids,
        source: source,
        destination: destination,
        note: 'drug status note',
      };
      const data = await drugPortfolioservices.bulkUpdateDrugPortfolioStatus(payload);
      // Map new ComponentIDs back into destination list for moved NDCs
      const createdMap = new Map<string, number>();
      data.created_components?.forEach(comp => createdMap.set(comp.NDC11, comp.ComponentID));

      if (destination === 'Active') {
        const active_gfc_idx = this.activeDrugs.findIndex(item => item.GFC === gfc);
        if (active_gfc_idx !== -1) {
          this.activeDrugs[active_gfc_idx].NDC11_list = this.activeDrugs[
            active_gfc_idx
          ].NDC11_list.map(ndcItem =>
            createdMap.has(ndcItem.NDC11)
              ? {
                  ...ndcItem,
                  Component_ID: createdMap.get(ndcItem.NDC11) || ndcItem.Component_ID,
                  loading: false,
                }
              : ndcItem
          );
        }
      }
      if (destination === 'Dormant') {
        const dormant_gfc_idx = this.dormantDrugs.findIndex(item => item.GFC === gfc);
        if (dormant_gfc_idx !== -1) {
          this.dormantDrugs[dormant_gfc_idx].NDC11_list = this.dormantDrugs[
            dormant_gfc_idx
          ].NDC11_list.map(ndcItem =>
            createdMap.has(ndcItem.NDC11)
              ? {
                  ...ndcItem,
                  Component_ID: createdMap.get(ndcItem.NDC11) || ndcItem.Component_ID,
                  loading: false,
                }
              : ndcItem
          );
        }
      }

      toast.success(data.message);
    } catch (error) {
      console.error('DrugPortfolioManagementStore => saveDrugStatus => ', error);
      toast.error('Something went wrong!');
      this.activeDrugs = originial_active_drugs;
      this.dormantDrugs = original_dormant_drugs;
    } finally {
      if (destination === 'Active') {
        const active_gfc_idx = this.activeDrugs.findIndex(item => item.GFC === gfc);
        if (active_gfc_idx !== -1) {
          this.activeDrugs[active_gfc_idx].NDC11_list.forEach(item => (item.loading = false));
        }
      }
      if (destination === 'Dormant') {
        const dormant_gfc_idx = this.dormantDrugs.findIndex(item => item.GFC === gfc);
        if (dormant_gfc_idx !== -1) {
          this.dormantDrugs[dormant_gfc_idx].NDC11_list.forEach(item => (item.loading = false));
        }
      }
    }
  };

  saveDrugStatus = async (
    comp_id: number,
    ndc: string,
    gfc: number,
    source: string,
    destination: string
  ) => {
    this.isDrugStatusUpdating = true;
    const originial_active_drugs = deepCopy(this.activeDrugs);
    const original_dormant_drugs = deepCopy(this.dormantDrugs);
    try {
      const payload = {
        component_ids: [comp_id],
        source: source,
        destination: destination,
        note: 'plm frontend note',
      };

      // moving from active to dormant
      if (source === 'Active' && destination === 'Dormant') {
        const active_gfc = this.activeDrugs.find(item => item.GFC === gfc);

        const active_drug =
          active_gfc && active_gfc.NDC11_list.find(item => item.Component_ID === comp_id);

        if (active_drug) {
          const dormant_gfc = this.dormantDrugs.find(item => item.GFC === active_gfc.GFC);

          if (!dormant_gfc?.NDC11_list.includes(active_drug)) {
            active_drug.loading = true;
            if (this.dormantDrugs.filter(item => item.GFC === gfc).length !== 0) {
              this.dormantDrugs.map(item =>
                item.GFC === active_gfc.GFC ? item.NDC11_list.push(active_drug) : item
              );
            } else {
              this.dormantDrugs.push({
                ...active_gfc,
                NDC11_list: [active_drug],
              });
            }
          }

          // removing the NDC from the active drug section
          this.activeDrugs = this.activeDrugs
            .map(item => {
              if (item.GFC === gfc) {
                const newNDC11List = item.NDC11_list.filter(itmNDC => itmNDC.NDC11 !== ndc);
                return newNDC11List.length > 0 ? { ...item, NDC11_list: newNDC11List } : null; // Return null if NDC11_list is empty
              } else {
                return item;
              }
            })
            .filter(item => item !== null); //Filter out null values
        }
      }

      // moving from dormant to active
      if (source === 'Dormant' && destination === 'Active') {
        const dormant_gfc = this.dormantDrugs.find(item => item.GFC === gfc);

        const dormant_drug =
          dormant_gfc && dormant_gfc.NDC11_list.find(item => item.Component_ID === comp_id);

        if (dormant_drug) {
          const active_gfc = this.activeDrugs.find(item => item.GFC === dormant_gfc.GFC);

          if (!active_gfc?.NDC11_list.includes(dormant_drug)) {
            dormant_drug.loading = true;

            if (this.activeDrugs.filter(item => item.GFC === gfc).length !== 0) {
              this.activeDrugs.map(item =>
                item.GFC === dormant_gfc.GFC ? item.NDC11_list.push(dormant_drug) : item
              );
            } else {
              this.activeDrugs.push({
                ...dormant_gfc,
                NDC11_list: [dormant_drug],
              });
            }
          }

          // removing the NDC from the dormant drug section
          this.dormantDrugs = this.dormantDrugs
            .map(item => {
              if (item.GFC === gfc) {
                const newNDC11List = item.NDC11_list.filter(itmNDC => itmNDC.NDC11 !== ndc);
                return newNDC11List.length > 0 ? { ...item, NDC11_list: newNDC11List } : null; // Return null if NDC11_list is empty
              } else {
                return item;
              }
            })
            .filter(item => item !== null); //Filter out null values
        }
      }

      const data = await drugPortfolioservices.bulkUpdateDrugPortfolioStatus(payload);
      // Update the new ComponentID for the moved NDC and reset loading
      const newComponentId = data.created_components?.[0]?.ComponentID;
      if (destination === 'Active') {
        const active_gfc = this.activeDrugs.find(item => item.GFC === gfc);
        if (active_gfc) {
          active_gfc.NDC11_list = active_gfc.NDC11_list.map(ndcItem =>
            ndcItem.NDC11 === ndc
              ? { ...ndcItem, Component_ID: newComponentId || ndcItem.Component_ID, loading: false }
              : ndcItem
          );
        }
      }
      if (destination === 'Dormant') {
        const dormant_gfc = this.dormantDrugs.find(item => item.GFC === gfc);
        if (dormant_gfc) {
          dormant_gfc.NDC11_list = dormant_gfc.NDC11_list.map(ndcItem =>
            ndcItem.NDC11 === ndc
              ? { ...ndcItem, Component_ID: newComponentId || ndcItem.Component_ID, loading: false }
              : ndcItem
          );
        }
      }
      toast.success(data.message);
    } catch (error) {
      console.log('DrugPortfolioManagementStore => saveDrugStatus => ', error);
      toast.error('Something went wrong!');
      this.activeDrugs = originial_active_drugs;
      this.dormantDrugs = original_dormant_drugs;
    } finally {
      this.isDrugStatusUpdating = false;
      if (destination === 'Active') {
        const active_gfc = this.activeDrugs.find(item => item.GFC === gfc);
        if (active_gfc) {
          this.activeDrugs = this.activeDrugs.map(item =>
            item.GFC === gfc
              ? {
                  ...item,
                  NDC11_list: item.NDC11_list.map(ndcItem =>
                    ndcItem.NDC11 === ndc && ndcItem.loading
                      ? { ...ndcItem, loading: false }
                      : ndcItem
                  ),
                }
              : item
          );
        }
      }
      if (destination === 'Dormant') {
        const dormant_gfc = this.dormantDrugs.find(item => item.GFC === gfc);
        if (dormant_gfc) {
          this.dormantDrugs = this.dormantDrugs.map(item =>
            item.GFC === gfc
              ? {
                  ...item,
                  NDC11_list: item.NDC11_list.map(ndcItem =>
                    ndcItem.NDC11 === ndc && ndcItem.loading
                      ? { ...ndcItem, loading: false }
                      : ndcItem
                  ),
                }
              : item
          );
        }
      }
    }
  };

  resetStore = () => {
    // Reset all observable properties to their initial values
    this.activeDrugs = [];
    this.dormantDrugs = [];
    this.inactiveDrugs = [];
    this.isActiveDrugsLoading = false;
    this.isDormantDrugsLoading = false;
    this.isInactiveDrugsLoading = false;
    this.isDrugStatusUpdating = false;
  };
}
